import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PlusCircle } from "lucide-react";
import { getInvoiceList, createInvoice, previewInvoice } from "../../services/invoice.service";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { getLeadsCustomer } from "../../services/lead.service";
import { productListOptions } from "../../services/products.service";
import Swal from "sweetalert2";
import { SWAL_SETTINGS, ROLE } from "../../../../utils/Constants";
import { getUser } from "../../../../utils/commonfunction";

const InvoiceCard = ({ invoiceId, customerName, date, amount }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white p-[15px] rounded-[10px] shadow-[0px_2px_24px_0px_rgba(0,0,0,0.05)] border !border-zinc-100 flex justify-between items-start transition-all duration-300 hover:shadow-xl cursor-pointer">
      <div className="flex-1 min-w-0">
        <h3 className="headings-h4 text-zinc-950 mb-1 leading-snug">
          {invoiceId}
        </h3>
        <p className="body-md text-zinc-500 truncate mt-2 mb-[10px]">
          {t('advertiser.invoice.customer')}: {customerName}
        </p>
        <p className="body-md text-zinc-500 mt-1">{date}</p>
      </div>

      <div className="text-right flex-shrink-0 ml-4">
        <p className="headings-h6 text-zinc-950">{amount}</p>
      </div>
    </div>
  );
};

function App() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userData = getUser(ROLE.ADVERTISER);

  const [list, setList] = useState([]);
  const [loader, setLoader] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [page, setPage] = useState(1);
  const [itemperpage, setItemperpage] = useState(10);
  const [sorting, setSorting] = useState({ createdAt: -1 });
  const [search, setSearch] = useState({});
  const [globalsearch, setGlobalSearch] = useState("");
  const [datalength, setDataLength] = useState(0);

  const [leadsCustomers, setLeadsCustomers] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [productOptions, setProductOptions] = useState([]);

  const [invoiceForm, setInvoiceForm] = useState({
    items: [],
    billing_address: "",
    shipping_address: "",
  });

  /* ---------------- Fetch Invoice List ---------------- */

  const fetchInvoices = async () => {
    try {
      setLoader(true);

      const formData = new FormData();
      formData.append("page", page);
      formData.append("per_page", itemperpage);
      formData.append("sort", JSON.stringify(sorting));
      formData.append("search", JSON.stringify(search));
      formData.append("global_search", globalsearch);

      const res = await getInvoiceList(formData);

      if (res.success) {
        setList(res.data?.list || []);
        setDataLength(res.data?.total_records || 0);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInvoices();
    }, 300);

    return () => clearTimeout(timer);
  }, [location, page, itemperpage, sorting, search, globalsearch]);

  /* ---------------- Fetch Customers ---------------- */

  const fetchLeadsCustomers = async () => {
    try {
      const resp = await getLeadsCustomer();

      if (resp.success) {
        const customers = resp.data?.customers || [];
        setLeadsCustomers(customers);

        if (customers.length > 0) {
          setSelectedLead(customers[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  /* ---------------- Fetch Products ---------------- */

  const fetchProductOptions = async () => {
    try {
      const resp = await productListOptions();

      if (resp.success) {
        setProductOptions(resp.data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    fetchLeadsCustomers();
    fetchProductOptions();
  }, []);

  /* ---------------- Invoice Item Handlers ---------------- */

  const handleInvoiceItemChange = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index][field] = value;

    setInvoiceForm({
      ...invoiceForm,
      items: newItems,
    });
  };

  const handleAddInvoiceItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [
        ...invoiceForm.items,
        {
          product_id: null,
          product_name: "",
          quantity: 1,
          hsn: "",
          price: 0,
          is_custom: true,
        },
      ],
    });
  };

  const handleRemoveInvoiceItem = (index) => {
    const newItems = [...invoiceForm.items];
    newItems.splice(index, 1);

    setInvoiceForm({
      ...invoiceForm,
      items: newItems,
    });
  };

  const calculateTotal = () => {
    return invoiceForm.items.reduce((acc, item) => {
      const price = parseFloat(item.price) || 0;
      const qty = parseFloat(item.quantity) || 1;
      return acc + price * qty;
    }, 0);
  };

  /* ---------------- Generate Invoice ---------------- */

  const handlePreviewInvoice = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;

    try {
      setPreviewLoading(true);
      const payload = {
        customer_id: selectedLead?._id,
        billing_address: invoiceForm.billing_address,
        shipping_address: invoiceForm.shipping_address,
        items: invoiceForm.items,
      };

      const resp = await previewInvoice(payload);
      if (resp.success && resp.data?.pdf_base64) {
        setPreviewPdfUrl(resp.data.pdf_base64);
        setShowPreview(true);
      } else {
        Swal.fire({
          icon: "error",
          text: resp.message || "Failed to generate preview",
          ...SWAL_SETTINGS,
        });
      }
    } catch (error) {
      console.error("Invoice preview error:", error);
      Swal.fire({
        icon: "error",
        text: error.message || "Something went wrong",
        ...SWAL_SETTINGS,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();

    if (!selectedLead) return;

    try {
      const payload = {
        customer_id: selectedLead?._id,
        billing_address: invoiceForm.billing_address,
        shipping_address: invoiceForm.shipping_address,
        items: invoiceForm.items,
      };

      const resp = await createInvoice(payload);

      if (resp.success) {
        Swal.fire({
          icon: "success",
          text: resp.message || t('advertiser.invoice.success_msg'),
          ...SWAL_SETTINGS,
        });

        document.getElementById("create-invoice").close();

        /* Reset Form */
        setInvoiceForm({
          items: [],
          billing_address: "",
          shipping_address: "",
        });

        /* Refresh Invoice List */
        fetchInvoices();
      } else {
        Swal.fire({
          icon: "error",
          text: resp.message || t('advertiser.invoice.fail_msg'),
          ...SWAL_SETTINGS,
        });
      }
    } catch (error) {
      console.error("Invoice creation error:", error);

      Swal.fire({
        icon: "error",
        text: error.message || "Something went wrong",
        ...SWAL_SETTINGS,
      });
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  /* ---------------- UI ---------------- */

  return (
    <>
      <div className="dashbaord-bg py-10">
        <div className="container">
          <div className="border-b border-gray-200 pb-[15px] mb-[30px] flex items-center justify-between flex-wrap gap-[15px]">
            <h1 className="headings-h1 text-zinc-950">{t('advertiser.invoice.heading')}</h1>

            <button
              className={`btn btn-pri !bg-[#FF63B5] !border-[#FF63B5] min-w-[175px] ${!userData?.gst_number ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() =>
                userData?.gst_number && document.getElementById("create-invoice").showModal()
              }
              disabled={!userData?.gst_number}
              title={!userData?.gst_number ? "GST number required to create invoice" : ""}
            >
              {t('advertiser.invoice.create_invoice')}
            </button>
          </div>

          <div className="grid grid-cols-1 gap-[15px] md:gap-[30px] md:grid-cols-2 lg:grid-cols-2">
            {loader ? (
              <div className="col-span-full text-center py-20">
                {t('advertiser.invoice.loading')}
              </div>
            ) : list.length === 0 ? (
              <div className="col-span-full text-center py-20">
                <h3 className="headings-h3 text-zinc-400">
                  {t('advertiser.invoice.no_invoices')}
                </h3>
              </div>
            ) : (
              list.map((invoice) => (
                <div
                  key={invoice._id}
                  onClick={() =>
                    navigate(`/advertiser/invoice-details?id=${invoice._id}`)
                  }
                >
                  <InvoiceCard
                    invoiceId={invoice.invoice_number}
                    customerName={invoice.customer?.name || "N/A"}
                    date={moment(invoice.createdAt).format("MMM DD, YYYY")}
                    amount={`INR ${invoice.total_amount}`}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create Invoice Modal */}

      <dialog id="create-invoice" className="modal p-[15px]">
        <div className={`modal-box ${showPreview ? "max-w-[820px]" : "max-w-[645px]"} !w-full transition-all duration-200`}>
          {showPreview ? (
            /* ─── INVOICE PREVIEW VIEW ─── */
            <div>
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
                <div>
                  <h2 className="headings-h2 text-left text-zinc-900 font-bold">
                    Invoice Preview
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">Review invoice details before generating</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="text-sm font-bold text-primary-500 hover:text-primary-600 flex items-center gap-1 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-all"
                >
                  ← Edit Invoice
                </button>
              </div>

              <div className="w-full bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                {previewPdfUrl ? (
                  <iframe
                    src={`${previewPdfUrl}#toolbar=0&navpanes=0`}
                    className="w-full h-[500px] border-0 rounded-xl"
                    title="Invoice Preview"
                  />
                ) : (
                  <div className="flex items-center justify-center h-[450px]">
                    <p className="text-gray-400 font-medium">Generating preview...</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-100 mt-4">
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="btn btn-outline flex-1 border-gray-300 text-gray-700"
                >
                  ← Back to Edit
                </button>

                <button
                  type="button"
                  onClick={handleGenerateInvoice}
                  className="btn btn-pri flex-1"
                >
                  {t('advertiser.invoice.generate_invoice')}
                </button>
              </div>
            </div>
          ) : (
            /* ─── INVOICE FORM VIEW ─── */
            <>
              <h2 className="headings-h1 text-left text-zinc-800 mb-4">
                {t('advertiser.invoice.create_invoice')}
              </h2>

              <form onSubmit={handleGenerateInvoice}>
                {/* Customer */}
                <div className="form-group">
                  <label>{t('advertiser.invoice.customer')}</label>

                  <select
                    className="form-control-custom"
                    value={selectedLead?._id || ""}
                    onChange={(e) => {
                      const selected = leadsCustomers.find(
                        (lead) => lead._id === e.target.value
                      );
                      setSelectedLead(selected || null);
                    }}
                  >
                    <option value="">{t('advertiser.invoice.customer')}</option>

                    {leadsCustomers.map((lead) => (
                      <option key={lead._id} value={lead._id}>
                        {lead.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Billing */}
                <div className="form-group">
                  <label>{t('advertiser.invoice.billing_address')}</label>

                  <input
                    type="text"
                    className="form-control-custom"
                    value={invoiceForm.billing_address}
                    onChange={(e) =>
                      setInvoiceForm({
                        ...invoiceForm,
                        billing_address: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Shipping */}
                <div className="form-group">
                  <label>{t('advertiser.invoice.shipping_address')}</label>

                  <input
                    type="text"
                    className="form-control-custom"
                    value={invoiceForm.shipping_address}
                    onChange={(e) =>
                      setInvoiceForm({
                        ...invoiceForm,
                        shipping_address: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Items */}
                <h3 className="headings-h5 mb-3">{t('advertiser.invoice.invoice_items')}</h3>

                {invoiceForm.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2.5 mb-3 items-center">
                    <div className="col-span-12 md:col-span-4">
                      <select
                        className="form-control-custom"
                        value={item.product_id || ""}
                        onChange={(e) => {
                          const product = productOptions.find(
                            (p) => p._id === e.target.value
                          );

                          if (product) {
                            handleInvoiceItemChange(index, "product_id", product._id);
                            handleInvoiceItemChange(index, "product_name", product.title);
                            handleInvoiceItemChange(index, "price", parseFloat(product.price) || 0);
                          }
                        }}
                      >
                        <option value="">{t('advertiser.invoice.invoice_items')}</option>

                        {productOptions.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-6 md:col-span-2">
                      <select
                        className="form-control-custom"
                        value={item.quantity}
                        onChange={(e) =>
                          handleInvoiceItemChange(
                            index,
                            "quantity",
                            Number(e.target.value)
                          )
                        }
                      >
                        {[1, 2, 3, 4, 5, 10].map((q) => (
                          <option key={q} value={q}>
                            {q}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-6 md:col-span-2">
                      <input
                        type="text"
                        placeholder={t('advertiser.invoice.hsn')}
                        className="form-control-custom"
                        value={item.hsn}
                        onChange={(e) =>
                          handleInvoiceItemChange(index, "hsn", e.target.value)
                        }
                      />
                    </div>

                    <div className="col-span-6 md:col-span-3">
                      <input
                        type="number"
                        min="0"
                        placeholder="Price"
                        className="form-control-custom"
                        value={item.price ?? ""}
                        onChange={(e) =>
                          handleInvoiceItemChange(index, "price", parseFloat(e.target.value) || 0)
                        }
                      />
                    </div>

                    <div className="col-span-6 md:col-span-1 flex justify-end md:justify-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveInvoiceItem(index)}
                        className="w-[44px] h-[44px] flex-[0,0,44px] rounded-full bg-primary-400 flex items-center justify-center text-white"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={handleAddInvoiceItem}
                  className="add-more-btn headings-h5 text-secondary-500 mb-3"
                >
                  {t('advertiser.invoice.add_more')}
                </button>

                {/* Total and Action Buttons */}
                <div className="text-center mb-6">
                  <p className="headings-h5">{t('advertiser.invoice.total')} {calculateTotal()}</p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handlePreviewInvoice}
                    disabled={previewLoading}
                    className="btn btn-outline flex-1 border-primary-400 text-primary-500 hover:bg-primary-50 font-bold"
                  >
                    {previewLoading ? "Loading Preview..." : "Preview Invoice"}
                  </button>

                  <button type="submit" className="btn btn-pri flex-1">
                    {t('advertiser.invoice.generate_invoice')}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
        <form method="dialog">
          {/* Close button at the bottom, outside the main modal box */}
          <button className="bg-white subtitle-2 flex items-center gap-[5px] px-[15px] py-[10px] rounded-full mt-4">
            <img src="/images/close.svg" alt="close" />
            <span className="subtitle-2 bold">{t('advertiser.my_team.close')}</span>
          </button>
        </form>
      </dialog>
    </>
  );
}

export default App;